// useDesignOnlineStore.js
// Design Online store with real API integration
import { defineStore } from 'pinia';
import { useAppStore } from '@/features/imprint/stores/useAppStore';
import designOnlineApi from '../services/designOnlineApi';

const STORAGE_KEY = 'design_online_v2';
const RETURN_KEY = 'design_online_return_v2';
const STORAGE_VERSION = 2;

export const useDesignOnlineStore = defineStore('designOnline', {
  state: () => ({
    // Design data
    tokenId: null,
    previewImage: null,
    editUrl: null,
    downloadUrl: null,
    pdfPreviewUrl: null,
    printReadyFileUrl: null,
    exResponseParam: null,

    // Studio config
    // productId is populated at runtime from DOM #ops_product_id (Shopify metafield custom.OPS_product_id)
    studioConfig: {
      productId: null,
    },
    
    // UI state
    stage: 'idle', // idle | chooser | preview | loading | error
    source: null, // create | upload | restored | null
    isLoading: false,
    error: null,
    
    // Approval
    isApproved: false,
    
    // Config URLs (from can-design API)
    uploadCenterUrl: null,
    customizeStudioUrl: null,
    
    // Support check cache
    isSupported: null,
    supportCheckDone: false,
    // Which product page the supportCheckDone refers to
    supportCheckProductKey: null,

    // Template selection (Variant A flow)
    templates: [],       // list from /product-templates
    selectedTemplate: null  // { pid, tempid, type, title, preview_url }
  }),

  getters: {
    hasDesign: (state) => !!(state.previewImage || state.pdfPreviewUrl || state.printReadyFileUrl),
    isReady: (state) => {
      const ready = state.tokenId && (state.previewImage || state.pdfPreviewUrl) && state.isApproved;
      // Only log when actually checking (avoid spam)
      if (typeof window !== 'undefined' && window.__DESIGN_ONLINE_DEBUG__) {
        console.log('[AddToCart] isReady getter called:', {
          hasTokenId: !!state.tokenId,
          hasPreview: !!(state.previewImage || state.pdfPreviewUrl),
          isApproved: state.isApproved,
          result: ready
        });
      }
      return ready;
    },
    lineItemProperties: (state) => {
      if (!state.tokenId) return null;
      return {
        token_id: state.tokenId,
        pdf_preview_url: state.pdfPreviewUrl || '',
        print_ready_file_url: state.printReadyFileUrl || '',
        preview_image: state.previewImage || ''
      };
    },
    // Keep for backward compatibility if referenced somewhere,
    // but do NOT use this for storage identity anymore.
    currentProductKey: () => window.location.pathname + window.location.search
  },

  actions: {
    // ===================
    // API ACTIONS
    // ===================

    /**
     * Check if product supports Design Online
     * Supports two-phase loading strategy:
     * - Phase 1 (fast): options.design_enabled=true, force_probe=false
     * - Phase 2 (lazy): options.force_probe=true to fetch URLs
     * 
     * @param {string} productId - Shopify product ID
     * @param {string} sku - Product SKU
     * @param {object} options - Check options
     * @param {boolean} options.design_enabled - Product has design_enabled metafield
     * @param {boolean} options.force_probe - Force Studio API call to get URLs
     */
    async checkSupport(productId, sku, options = {}) {
      const currentKey = this.getStableProductKey();
      const hasUrls = !!this.uploadCenterUrl || !!this.customizeStudioUrl;
      console.log('[DO_NAV] 🔍 checkSupport() called:', {
        productId,
        sku,
        options,
        supportCheckDone: this.supportCheckDone,
        supportCheckProductKey: this.supportCheckProductKey,
        currentKey,
        hasUrls,
        currentProductKey: window.location.pathname
      });

      // ✅ ONLY skip if:
      // - check was done,
      // - for the same product page,
      // - and we are not forcing probe
      if (this.supportCheckDone && this.supportCheckProductKey === currentKey && !options.force_probe) {
        console.log('[DO_NAV] Support already checked for THIS product, skipping (isSupported:', this.isSupported, ')');
        return this.isSupported;
      }

      // ✅ If supportCheckDone=true but for a DIFFERENT product, treat as not checked
      if (this.supportCheckDone && this.supportCheckProductKey !== currentKey && !options.force_probe) {
        console.log('[DO_NAV] Support was checked for a different product. Re-checking for current product...');
      }

      console.log('[DO_NAV] Proceeding with support check...');
      this.isLoading = true;
      this.error = null;

      try {
        console.log('[DO_NAV] Calling designOnlineApi.canDesign...');
        const result = await designOnlineApi.canDesign(productId, sku, options);
        
        console.log('[DO_NAV] API response:', {
          ok: result.ok,
          source: result.source,
          upload_center_url: result.upload_center_url,
          customize_studio_url: result.customize_studio_url
        });
        
        this.isSupported = result.ok;
        
        // Store URLs when they arrive (from force_probe=1)
        if (result.upload_center_url) {
          this.uploadCenterUrl = result.upload_center_url;
          console.log('[DO_NAV] ✅ uploadCenterUrl saved');
        }
        if (result.customize_studio_url) {
          this.customizeStudioUrl = result.customize_studio_url;
          console.log('[DO_NAV] ✅ customizeStudioUrl saved');
        }
        
        this.supportCheckDone = true;
        this.supportCheckProductKey = currentKey;
        console.log('[DO_NAV] ✅ supportCheckDone = true');

        console.log('[DO_NAV] Support check complete:', {
          supported: this.isSupported,
          source: result.source,
          hasUploadUrl: !!this.uploadCenterUrl,
          hasCustomizeUrl: !!this.customizeStudioUrl,
          forceProbe: options.force_probe || false
        });

        return this.isSupported;
      } catch (error) {
        console.error('[DesignOnline] Support check failed:', error);
        console.warn('[DesignOnline] Assuming supported (fallback mode)');
        
        // FALLBACK: If API fails, assume supported
        this.isSupported = true;
        this.supportCheckDone = true;
        this.supportCheckProductKey = currentKey;
        this.error = null;  // Don't show error to user
        
        return true;
      } finally {
        this.isLoading = false;
      }
    },

    // Fetch templates and either redirect immediately (1 template)
    // or show template selector (multiple templates).
    async startCreate(sku) {
      console.log('[DO_NAV] 🎨 startCreate() START', { sku });

      const productId = this.studioConfig.productId;
      if (!productId) {
        this.error = 'Missing OPS product ID — initProductId() was not called';
        console.error('[DO_NAV] ❌', this.error);
        return;
      }

      this.isLoading = true;
      this.error = null;
      this.templates = [];
      this.selectedTemplate = null;

      try {
        const templates = await designOnlineApi.getProductTemplates(productId, sku);
        console.log('[DO_NAV] Templates loaded:', templates);

        if (!templates || templates.length === 0) {
          throw new Error('No templates available for this product');
        }

        // Always redirect with the first (default) template
        await this.redirectWithTemplate(sku, templates[0]);
      } catch (error) {
        console.error('[DesignOnline] startCreate failed:', error);
        this.error = 'Failed to load design templates. Please try again.';
        this.isLoading = false;
      }
    },

    // Redirect to Studio with a specific template's pid/tempid.
    async redirectWithTemplate(sku, template) {
      const productId = this.studioConfig.productId;
      console.log('[DO_NAV] redirectWithTemplate()', template);

      this.isLoading = true;
      this.error = null;

      try {
        const redirectUrl = await designOnlineApi.getRedirectUrl({
          productId,
          sku,
          pid: template.pid,
          tempid: template.tempid,
          type: template.type || 'S',
          returnPage: this.getCleanReturnPage(),
          returnUrl: window.location.origin,
          userId: this.getCurrentUserId()
        });

        if (redirectUrl) {
          this.saveBeforeRedirect('create');
          window.location.href = redirectUrl;
        } else {
          throw new Error('No redirect URL received');
        }
      } catch (error) {
        console.error('[DesignOnline] redirectWithTemplate failed:', error);
        this.error = 'Failed to start Design Online. Please try again.';
        this.isLoading = false;
      }
    },

    setStudioConfig(patch) {
      this.studioConfig = { ...this.studioConfig, ...(patch || {}) };
    },

    // Read OPS product ID from DOM and store it in studioConfig.
    // Call this once on component mount before checkSupport().
    // Returns the product ID string or null if not found.
    initProductId() {
      const id = document.getElementById('ops_product_id')?.textContent?.trim() || null;
      if (id) {
        this.studioConfig = { ...this.studioConfig, productId: id };
        console.log('[DesignOnline] OPS productId from DOM:', id);
      } else {
        console.warn('[DesignOnline] #ops_product_id not found or empty — Design Online not available for this product');
        this.isSupported = false;
        this.supportCheckDone = true;
        this.supportCheckProductKey = this.getStableProductKey();
      }
      return id;
    },

    async startUpload(productId, sku) {
      if (!this.uploadCenterUrl) {
        this.error = 'Upload center not available';
        return;
      }

      this.isLoading = true;
      this.error = null;

      try {
        const redirectUrl = await designOnlineApi.getUploadRedirectUrl({
          productId,
          sku,
          uploadCenterUrl: this.uploadCenterUrl,
          returnPage: this.getCleanReturnPage(),
          returnUrl: window.location.origin,
          userId: this.getCurrentUserId()
        });

        if (redirectUrl) {
          this.saveBeforeRedirect('upload');
          sessionStorage.setItem('studio_upload_flow_v1', '1');
          window.location.href = redirectUrl;
        } else {
          throw new Error('No redirect URL received');
        }
      } catch (error) {
        console.error('[DesignOnline] Start upload failed:', error);
        this.error = 'Failed to open upload center. Please try again.';
        this.isLoading = false;
      }
    },

    async handleReturn(tokenId, exResponseParam) {
      if (!tokenId || !exResponseParam) {
        console.warn('[DesignOnline] Missing return parameters');
        return false;
      }

      this.exResponseParam = exResponseParam;
      this.saveToStorage();

      this.isLoading = true;
      this.error = null;
      this.stage = 'loading';

      try {
        const designData = await designOnlineApi.getTemplateData({
          tokenId,
          exResponseParam,
          returnPage: this.getCleanReturnPage(),
          returnUrl: window.location.origin,
          userId: this.getCurrentUserId()
        });

        if (!designData || !designData.preview_image) {
          console.warn('[DesignOnline] Design expired or invalid');
          this.resetToChooser();
          return false;
        }

        // ⚠️ CRITICAL DEBUG: Log RAW response from backend (plain values only)
        console.log('[DesignOnline] 📦 RAW /template response:', JSON.parse(JSON.stringify(designData)));
        console.log('[DesignOnline] 🔑 token_id from response:', designData.token_id);
        console.log('[DesignOnline] 🎨 design_url from response:', designData.design_url);
        console.log('[DesignOnline] 🖼️ preview_image from response:', designData.preview_image?.substring(0, 100) + '...');
        
        this.tokenId = designData.token_id || tokenId;
        this.previewImage = designData.preview_image;
        this.editUrl = designData.design_url;
        this.downloadUrl = designData.pdf_preview_url;
        this.pdfPreviewUrl = designData.pdf_preview_url;
        this.printReadyFileUrl = designData.print_ready_file_url;
        this.stage = 'preview';
        this.source = this.getSourceFromStorage() || 'create';
        
        // ✅ DEBUG: Log parsed data (avoid reactive objects)
        console.log('[DesignOnline] 🎉 Data saved to store:', {
          tokenId: String(this.tokenId),
          hasPreview: !!this.previewImage,
          editUrl: String(this.editUrl),
          downloadUrl: String(this.downloadUrl),
          source: String(this.source)
        });
        
        // ⚠️ Detailed editUrl analysis
        if (this.editUrl) {
          try {
            const urlCheck = new URL(this.editUrl, window.location.origin);
            console.log('[DesignOnline] 🔍 Edit URL parameters:', {
              fullUrl: String(this.editUrl),
              pid: urlCheck.searchParams.get('pid'),
              tempid: urlCheck.searchParams.get('tempid'),
              type: urlCheck.searchParams.get('type'),
              hasTokenInUrl: urlCheck.searchParams.has('token_id'),
              allParams: JSON.stringify(Array.from(urlCheck.searchParams.entries()))
            });
          } catch (e) {
            console.error('[DesignOnline] ❌ Could not parse editUrl:', e.message);
            console.log('[DesignOnline] editUrl value:', String(this.editUrl));
          }
        } else {
          console.warn('[DesignOnline] ⚠️ editUrl is EMPTY or NULL!');
        }
        
        this.saveToStorage();
        this.cleanUrlParameters();

        console.log('[DesignOnline] Return handled successfully:', {
          tokenId: this.tokenId,
          hasPreview: !!this.previewImage,
          source: this.source
        });

        return true;
      } catch (error) {
        console.error('[DesignOnline] Handle return failed:', error);
        this.error = 'Failed to load design data';
        this.stage = 'error';
        return false;
      } finally {
        this.isLoading = false;
        sessionStorage.removeItem('studio_upload_flow_v1');
      }
    },

    /**
     * Open Edit Design mode
     * ✅ EXACT COPY from old working code - DO NOT modify!
     * Uses design_url from Studio API directly with return parameters only
     */
    openEdit() {
      if (!this.editUrl) {
        console.warn('[DesignOnline] No edit URL available');
        this.error = 'Edit URL not available';
        return;
      }

      try {
        console.log('[DesignOnline] 📊 Opening edit with editUrl:', String(this.editUrl));

        // ✅ CRITICAL: Save ALL data before redirect to Edit Design
        console.log('[EditDesign] 💾 Saving all data before redirect...');
        
        // Import stores dynamically
        import('@/features/imprint/stores/useNewImprintStore').then(({ useNewImprintStore }) => {
          const newImprintStore = useNewImprintStore();
          if (newImprintStore.imprints.length > 0) {
            newImprintStore.saveToSessionStorage();
            console.log('[EditDesign] ✅ Saved newImprintStore');
          }
        });
        
        import('@/features/imprint/stores/useSelectionStore').then(({ useSelectionStore }) => {
          const selectionStore = useSelectionStore();
          selectionStore.saveToSessionStorage();
          console.log('[EditDesign] ✅ Saved selectionStore');
        });
        
        // Save Design Online state
        this.saveToStorage();
        console.log('[EditDesign] ✅ Saved designOnlineStore');

        const url = new URL(this.editUrl, window.location.origin);
        const cleanReturnPage = this.getCleanReturnPage();

        // Add ONLY return parameters
        url.searchParams.set('return_url', `${location.protocol}//${location.host}`);
        url.searchParams.set('return_page', cleanReturnPage);
        url.searchParams.set('user_id', this.getCurrentUserId());
        url.searchParams.set('lang_id', 'EN');
        // ✅ FIX: re-attach ex_response_param so Studio can validate the return
        if (this.exResponseParam) {
          url.searchParams.set('ex_response_param', this.exResponseParam);
        }

        console.log('[DesignOnline] ✅ Final edit URL:', String(url.toString()));

        this.saveBeforeRedirect('edit');
        
        // ✅ Wait a bit for async saves to complete
        setTimeout(() => {
          console.log('[EditDesign] 🚀 Redirecting to Studio...');
          window.location.href = url.toString();
        }, 100);
      } catch (error) {
        console.error('[DesignOnline] ❌ Failed to open edit:', error);
        this.error = 'Failed to open edit';
      }
    },

    openDownload() {
      if (!this.downloadUrl) {
        console.warn('[DesignOnline] No download URL available');
        return;
      }
      window.open(this.downloadUrl, '_blank');
    },

    /**
      * Stable page identity for storage/restore.
      * We intentionally use pathname only to avoid Studio return params
      * breaking restore (product key mismatch).
      */
      getStableProductKey() {
        try {
          const u = new URL(window.location.href);
          return u.pathname; // ✅ pathname only
        } catch (e) {
          return window.location.pathname;
        }
    },

    // ===================
    // STORAGE ACTIONS
    // ===================

    saveToStorage() {
      const data = {
        tokenId: this.tokenId,
        previewImage: this.previewImage,
        editUrl: this.editUrl,
        pdfPreviewUrl: this.pdfPreviewUrl,
        printReadyFileUrl: this.printReadyFileUrl,
        source: this.source,
        productKey: this.getStableProductKey(),
        timestamp: Date.now(),
        version: STORAGE_VERSION,
        exResponseParam: this.exResponseParam,
        isApproved: this.isApproved  // ✅ Save approval state
      };

      try {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(data));
        console.log('[DesignOnline] State saved to storage (isApproved:', this.isApproved, ')');
      } catch (error) {
        console.error('[DesignOnline] Failed to save to storage:', error);
      }
    },

    restoreFromStorage() {
      try {
        console.log('[DO_NAV] 💾 restoreFromStorage() START');
        console.log('[DO_NAV] Current stage before restore:', this.stage);
        console.log('[DO_NAV] Current productKey:', window.location.pathname);
        
        const raw = sessionStorage.getItem(STORAGE_KEY);
        if (!raw) {
          console.log('[DO_NAV] ❌ No storage data found in sessionStorage');
          return false;
        }
        
        console.log('[DO_NAV] Storage data found, parsing...');
        const data = JSON.parse(raw);
        console.log('[AUTO_SWITCH] Storage data:', {
          version: data.version,
          tokenId: data.tokenId,
          hasPreview: !!data.previewImage,
          age: Math.round((Date.now() - data.timestamp) / 1000) + 's'
        });

        if (data.version !== STORAGE_VERSION) {
          console.warn('[DO_NAV] ❌ Storage version mismatch - clearing');
          this.clearStorage();
          return false;
        }
        
        // Guard: only restore for same product page (pathname)
        const currentKey = this.getStableProductKey();
        console.log('[DO_NAV] Product key check:', {
          stored: data.productKey,
          current: currentKey,
          match: data.productKey === currentKey
        });
        
        if (data.productKey && data.productKey !== currentKey) {
          console.warn('[DO_NAV] ❌ Product key mismatch - clearing storage');
          this.clearStorage();
          return false;
        }

        const age = Date.now() - data.timestamp;
        console.log('[DO_NAV] Data age:', Math.round(age / 1000), 'seconds');
        
        if (age > 10 * 60 * 1000) {
          console.warn('[DO_NAV] ❌ Storage data expired (> 10min) - clearing');
          this.clearStorage();
          return false;
        }

        if (!data.tokenId || !data.previewImage) {
          console.warn('[DO_NAV] ❌ Invalid storage data (missing tokenId or previewImage) - clearing');
          this.clearStorage();
          return false;
        }
        
        console.log('[DO_NAV] ✅ All validations passed - restoring data...');

        console.log('[AUTO_SWITCH] 👍 All validations passed - restoring state');
        console.log('[AUTO_SWITCH] Current stage:', this.stage, '→ Will change to: preview');
        
        this.tokenId = data.tokenId;
        this.previewImage = data.previewImage;
        this.editUrl = data.editUrl;
        this.downloadUrl = data.pdfPreviewUrl;
        this.pdfPreviewUrl = data.pdfPreviewUrl;
        this.printReadyFileUrl = data.printReadyFileUrl;
        this.source = data.source || 'restored';
        this.stage = 'preview'; // ✅ This should trigger watcher!
        this.exResponseParam = data.exResponseParam || null;
        this.isApproved = data.isApproved || false;  // ✅ Restore approval state

        console.log('[DO_NAV] ✅ restoreFromStorage() SUCCESS - State restored:', {
          stage: this.stage,
          hasDesign: this.hasDesign,
          tokenId: this.tokenId,
          isApproved: this.isApproved
        });
        return true;
      } catch (error) {
        console.error('[DO_NAV] ❌ restoreFromStorage() FAILED:', error);
        return false;
      }
    },

    saveBeforeRedirect(source) {
      const data = {
        source,
        timestamp: Date.now(),
        productKey: this.getStableProductKey()
      };

      try {
        sessionStorage.setItem(RETURN_KEY, JSON.stringify(data));
      } catch (error) {
        console.error('[DesignOnline] Failed to save return data:', error);
      }
    },

    getSourceFromStorage() {
      try {
        const raw = sessionStorage.getItem(RETURN_KEY);
        if (!raw) return null;
        const data = JSON.parse(raw);
        return data.source || null;
      } catch (error) {
        return null;
      }
    },

    clearStorage() {
      try {
        sessionStorage.removeItem(STORAGE_KEY);
        sessionStorage.removeItem(RETURN_KEY);
        sessionStorage.removeItem('studio_payload');
        sessionStorage.removeItem('studio_return');
        localStorage.removeItem('studio_payload');
        localStorage.removeItem('studio_return');
        console.log('[DesignOnline] Storage cleared');
      } catch (error) {
        console.error('[DesignOnline] Failed to clear storage:', error);
      }
    },

    // ===================
    // STATE MANAGEMENT
    // ===================

    setApproval(approved) {
      this.isApproved = approved;
      console.log('[DesignOnline] Approval:', approved ? '✅ Approved' : '⏸️ Not approved');
      console.log('[AddToCart] 📦 setApproval() called with:', approved);
      console.log('[AddToCart] Store state after setApproval:', {
        isApproved: this.isApproved,
        hasTokenId: !!this.tokenId,
        hasPreview: !!(this.previewImage || this.pdfPreviewUrl),
        isReady: this.isReady
      });
      
      // Save to storage to persist approval state
      if (this.tokenId) {
        this.saveToStorage();
        console.log('[AddToCart] 💾 Approval state saved to storage');
      }
    },

    resetToChooser() {
      this.tokenId = null;
      this.previewImage = null;
      this.editUrl = null;
      this.downloadUrl = null;
      this.pdfPreviewUrl = null;
      this.printReadyFileUrl = null;
      this.source = null;
      this.templates = [];
      this.selectedTemplate = null;
      
      // ✅ CRITICAL: Set to 'idle' instead of 'chooser' to prevent watcher trigger
      // Watcher in CustomizeProductSection watches for stage = 'preview' to auto-scroll
      // Using 'idle' prevents unwanted auto-scroll after Add to Cart or product change
      this.stage = 'idle';
      
      this.isApproved = false;
      this.error = null;
      this.clearStorage();
      console.log('[DesignOnline] Reset to idle (prevents auto-scroll)');
    },

    fullReset() {
      console.log('[DO_NAV] 🔄 fullReset() START');
      console.log('[DO_NAV] State before reset:', {
        stage: this.stage,
        isSupported: this.isSupported,
        supportCheckDone: this.supportCheckDone,
        hasUploadUrl: !!this.uploadCenterUrl,
        hasCustomizeUrl: !!this.customizeStudioUrl,
        studioConfig: JSON.parse(JSON.stringify(this.studioConfig))
      });
      
      this.resetToChooser();
      this.supportCheckDone = false;
      this.isSupported = null;
      this.uploadCenterUrl = null;
      this.customizeStudioUrl = null;
      this.exResponseParam = null;
      
      console.log('[DO_NAV] 🔄 fullReset() END - State after reset:', {
        stage: this.stage,
        isSupported: this.isSupported,
        supportCheckDone: this.supportCheckDone,
        supportCheckProductKey: this.supportCheckProductKey,
        hasUploadUrl: !!this.uploadCenterUrl,
        hasCustomizeUrl: !!this.customizeStudioUrl,
        studioConfig: JSON.parse(JSON.stringify(this.studioConfig))
      });
    },

    initialize() {
      console.log('[DO_NAV] 🚀 initialize() called - current stage:', this.stage);
      if (this.stage === 'idle') {
        console.log('[DO_NAV] Setting stage: idle → chooser');
        this.stage = 'chooser';
      } else {
        console.log('[DO_NAV] Stage already', this.stage, '- skipping initialization');
      }
      console.log('[DO_NAV] initialize() END - stage:', this.stage);
    },

    // ===================
    // HELPER METHODS
    // ===================

    getCleanReturnPage() {
      try {
        const url = new URL(window.location.href);
        // ✅ CRITICAL:
        // return_page MUST NOT include ANY query params (e.g. _pos/_psq/_ss/_v from search results),
        // otherwise Studio may append token_id using "?" again → invalid URL with double "?".
        // Always return canonical path only (+ anchor hash if present).
        const pathOnly = url.pathname;

        let hash = url.hash || '';
        if (hash.startsWith('#')) hash = hash.slice(1);
        const [anchor] = hash.split('?', 1);
        const cleanHash = anchor ? ('#' + anchor) : '';
        return pathOnly + cleanHash;
      } catch (error) {
        // Fallback: also DO NOT include search params
        return window.location.pathname;
      }
    },

    getCurrentUserId() {
      const shopifyUserId = window.ShopifyAnalytics?.meta?.page_customer_id;
      if (shopifyUserId) return shopifyUserId;

      const appStore = useAppStore();
      if (appStore.userId) return appStore.userId;

      const KEY = 'guest_uuid';
      let id = localStorage.getItem(KEY);
      if (!id) {
        id = crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(16).slice(2)}`;
        localStorage.setItem(KEY, id);
      }
      return id;
    },

    cleanUrlParameters() {
      try {
        // ✅ Extra safety:
        // Handle malformed URLs like "...?_v=1.0?token_id=...&ex_response_param=..."
        // where token params are appended with a second "?".
        const rawHref = window.location.href;
        const badTokenIndex = rawHref.indexOf('?token_id=');
        if (badTokenIndex !== -1) {
          const hashIndex = rawHref.indexOf('#');
          const hashPart = hashIndex !== -1 ? rawHref.slice(hashIndex) : '';
          const base = rawHref.slice(0, badTokenIndex);
          window.history.replaceState({}, '', base + hashPart);
          console.log('[DesignOnline] Malformed token params stripped from URL');
          return;
        }

        const url = new URL(window.location.href);
        const search = new URLSearchParams(url.search);
        search.delete('token_id');
        search.delete('ex_response_param');
        search.delete('ex_response_params');

        const rawHash = url.hash.startsWith('#') ? url.hash.slice(1) : url.hash;
        const [hashAnchor, hashQuery] = rawHash.split('?', 2);
        let newHash = hashAnchor || '';
        
        if (hashQuery) {
          const hq = new URLSearchParams(hashQuery);
          hq.delete('token_id');
          hq.delete('ex_response_param');
          hq.delete('ex_response_params');
          const tail = hq.toString();
          newHash = tail ? `${newHash}?${tail}` : newHash;
        }

        const clean = url.origin + url.pathname + 
          (search.toString() ? '?' + search.toString() : '') +
          (newHash ? '#' + newHash : '');

        window.history.replaceState({}, '', clean);
        console.log('[DesignOnline] URL parameters cleaned');
      } catch (error) {
        console.error('[DesignOnline] Failed to clean URL:', error);
      }
    },

    readReturnParamsFromUrl() {
      try {
        const search = new URLSearchParams(window.location.search);
        let tokenId = search.get('token_id');
        let exParam = search.get('ex_response_param') || search.get('ex_response_params');

        // ✅ Fallback for malformed "...?_pos=...?_token_id=..."
        // In such case, token params are not real searchParams; they are inside href tail after "?token_id=".
        if (!tokenId || !exParam) {
          const href = window.location.href;
          const idx = href.indexOf('?token_id=');
          if (idx !== -1) {
            const tail = href.slice(idx + 1); // "token_id=...&ex_response_param=...(#maybe)"
            const tailBeforeHash = tail.split('#', 1)[0];
            const tailParams = new URLSearchParams(tailBeforeHash);
            tokenId = tokenId || tailParams.get('token_id');
            exParam =
                exParam ||
                tailParams.get('ex_response_param') ||
                tailParams.get('ex_response_params');
            console.warn('[DesignOnline] Detected malformed return params in URL; recovered via fallback');
          }
        }

        if (!tokenId || !exParam) {
          const hash = window.location.hash.startsWith('#') ? window.location.hash.slice(1) : window.location.hash;
          const hashIndex = hash.indexOf('?');
          
          if (hashIndex >= 0) {
            const hashParams = new URLSearchParams(hash.slice(hashIndex + 1));
            tokenId = tokenId || hashParams.get('token_id');
            exParam = exParam || hashParams.get('ex_response_param') || hashParams.get('ex_response_params');
          }
        }

        if (exParam) {
          const cut = exParam.indexOf('?token_id=');
          if (cut !== -1) exParam = exParam.slice(0, cut);
        }

        return { tokenId, exParam };
      } catch (error) {
        console.error('[DesignOnline] Failed to read URL params:', error);
        return { tokenId: null, exParam: null };
      }
    }
  }
});
